import { useState, useEffect, useCallback } from 'react';
import { API_BASE } from '../config';
import { formatDate } from '../utils/formatDate';
const BASE = API_BASE;

const fmtNum = (n) => (n || 0).toLocaleString('en-IN');
const fmtINR = (n) => '₹' + (n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const FIELD_LABELS = { ean_number: 'EAN', ean_code: 'EAN', product_name: 'SKU', case_pack: 'Case Pack', case_qty: 'Case Pack', mrp: 'MRP' };

const SOURCE_LABEL = { distributor_po: 'Distributor', institutional_order: 'Institutional' };
const SOURCE_COLOR = { distributor_po: { bg: '#E5EEF7', fg: '#4A6FA5' }, institutional_order: { bg: '#F7E5EA', fg: '#B05770' } };

function SourceBadge({ source }) {
    const c = SOURCE_COLOR[source] || { bg: '#F1EEF6', fg: '#6B6379' };
    return (
        <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 9px', borderRadius: 999, background: c.bg, color: c.fg, whiteSpace: 'nowrap' }}>
            {SOURCE_LABEL[source] || source || 'Unknown'}
        </span>
    );
}

// Universal Proforma Invoice queue — shows pending (order_type set, no
// matching real SO yet) POs from BOTH Distributor PO and Institutional
// Orders, distinguished with a source badge everywhere. Mobile-first:
// stacked cards instead of a fixed-column desktop grid, a full-screen
// detail view instead of a small centered modal — see
// plans/institutional-orders-plan.md for the full rationale. A row
// disappears from here automatically the moment a real Sales Order
// exists for it — there is no manual "mark as done" action.
export default function ProformaInvoice({ user }) {
    const [rows, setRows]       = useState([]);
    const [loading, setLoading] = useState(false);
    const [search, setSearch]   = useState('');
    const [viewTarget, setViewTarget] = useState(null); // po_number or null
    const [viewData, setViewData]     = useState(null); // { header, lines } or null
    const [viewLoading, setViewLoading] = useState(false);

    const loadData = useCallback(async () => {
        setLoading(true);
        try {
            const res  = await fetch(`${BASE}/po-tracker?view=pending_so`);
            const data = await res.json();
            setRows(Array.isArray(data.rows) ? data.rows : []);
        } catch (e) { console.error(e); }
        finally { setLoading(false); }
    }, []);

    useEffect(() => {
        loadData();
        const onFocus = () => loadData();
        window.addEventListener('focus', onFocus);
        return () => window.removeEventListener('focus', onFocus);
    }, [loadData]);

    // Deep-link from a notification click (?q=<po_number>).
    useEffect(() => {
        const q = new URLSearchParams(window.location.search).get('q');
        if (q) setSearch(q);
    }, []);

    const filtered = rows.filter(r => {
        const q = search.trim().toLowerCase();
        if (!q) return true;
        return (r.po_number || '').toLowerCase().includes(q)
            || (r.party_name || '').toLowerCase().includes(q)
            || (r.kam_name || '').toLowerCase().includes(q);
    });

    async function openView(poNumber) {
        setViewTarget(poNumber);
        setViewData(null);
        setViewLoading(true);
        markVisited(poNumber);
        try {
            const res  = await fetch(`${BASE}/proforma-invoice?action=detail&po_number=${encodeURIComponent(poNumber)}`);
            const data = await res.json();
            if (res.ok) setViewData(data);
        } catch (e) { console.error(e); }
        finally { setViewLoading(false); }
    }
    function closeView() { setViewTarget(null); setViewData(null); }

    // Fire-and-forget — shared per-user "visited" tracking (record-visits.js)
    // that drives this section's sidebar badge count in AccountsLayout.jsx.
    // Section key kept as-is (distributor_performa_invoice) even though the
    // page is now universal — see permissionConfig.js for why the key stays.
    function markVisited(poNumber) {
        fetch(`${BASE}/record-visits?action=mark`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ username: user?.username, section: 'distributor_performa_invoice', record_key: poNumber }),
        }).catch(() => {});
    }

    const viewed = viewData;
    const isInstitutional = viewed?.header?.po_source === 'institutional_order';

    return (
        <div className="pi-page" data-tutorial="proforma-table" style={S.page}>
            <style>{EMBEDDED_CSS}</style>
            <div style={S.headerRow}>
                <h1 style={S.h1}>Proforma Invoice</h1>
                <input
                    className="pi-search"
                    style={S.search}
                    placeholder="PO number, party, KAM..."
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                />
            </div>
            <p style={S.sub}>Pending POs still waiting on a real Sales Order or Proforma Invoice — clears itself once one arrives.</p>

            {loading ? (
                <p style={S.empty}>Loading...</p>
            ) : filtered.length === 0 ? (
                <p style={S.empty}>Nothing pending — every submitted PO has a matching SO.</p>
            ) : (
                <div style={S.cardList}>
                    {filtered.map(r => (
                        <button key={r.id} className="pi-card" style={S.card} onClick={() => openView(r.po_number)}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
                                <div style={{ minWidth: 0 }}>
                                    <div style={{ fontWeight: 700, fontSize: 14 }}>{r.po_number}</div>
                                    <div style={{ fontSize: 13, color: '#5B5468', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.party_name}</div>
                                </div>
                                <SourceBadge source={r.po_source} />
                            </div>
                            <div style={S.cardMetaRow}>
                                <span>{r.kam_name || '—'}</span>
                                <span>{formatDate(r.po_date)}</span>
                            </div>
                            <div style={S.cardMetaRow}>
                                <span>{r.order_type ? <span style={{ ...S.chip, background: r.order_type === 'Sales Order' ? '#E7F0E8' : '#F1EEF6', color: r.order_type === 'Sales Order' ? '#5C7E60' : '#6B6379' }}>{r.order_type}</span> : '—'}</span>
                                <span style={{ fontWeight: 700 }}>{fmtNum(r.po_units)} pcs · {fmtINR(r.po_amount)}</span>
                            </div>
                        </button>
                    ))}
                </div>
            )}

            {viewTarget && (
                <div style={S.overlay} onClick={closeView}>
                    <div style={S.sheet} onClick={e => e.stopPropagation()}>
                        <div style={S.sheetHeader}>
                            <div style={{ minWidth: 0 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                    <h3 style={{ margin: 0, fontSize: 17 }}>PO {viewTarget}</h3>
                                    {viewed?.header?.po_source && <SourceBadge source={viewed.header.po_source} />}
                                    {viewed?.header?.remark && (
                                        <span style={{
                                            display: 'inline-flex', alignItems: 'center', gap: 5,
                                            background: '#C62828', color: '#fff', border: '1px solid #C62828',
                                            borderRadius: 20, padding: '3px 10px', fontSize: 12, fontWeight: 700,
                                        }}>
                                            ⚠️ {viewed.header.remark}
                                        </span>
                                    )}
                                </div>
                                {viewed?.header?.party_name && <p style={{ margin: '4px 0 0', fontSize: 13, color: '#5B5468' }}>{viewed.header.party_name}</p>}
                            </div>
                            <button style={S.closeBtn} onClick={closeView}>✕</button>
                        </div>

                        {!viewed ? (
                            <p style={{ ...S.empty, padding: '0 20px 20px' }}>{viewLoading ? 'Loading...' : 'No details found.'}</p>
                        ) : (() => {
                            const regionKeys = viewed.lines.flatMap(li => Object.keys(li.region_qty || {}));
                            const hasRegions = !isInstitutional && new Set(regionKeys).size > 0;
                            const uniqueRegionKeys = [...new Set(regionKeys)];
                            const isProforma = viewed.header.order_type === 'Proforma Invoice';
                            const taxTotal = isInstitutional && isProforma
                                ? viewed.lines.reduce((s, li) => s + Number(li.po_amount || 0) * Number(li.gst_percent || 0) / 100, 0)
                                : 0;
                            const netAmount = Number(viewed.header.po_amount || 0) + taxTotal;
                            return (
                                <>
                                    <div style={S.fieldGrid}>
                                        {[
                                            { label: 'Order Type', value: viewed.header.order_type ?? '—', accent: true },
                                            { label: 'PO Date', value: formatDate(viewed.header.po_date) },
                                            { label: 'Location', value: viewed.header.location ?? '—' },
                                            { label: 'Total Units', value: fmtNum(viewed.header.po_units) },
                                            ...(isInstitutional && isProforma ? [
                                                { label: 'Total Amount', value: fmtINR(viewed.header.po_amount) },
                                                { label: 'Tax', value: fmtINR(taxTotal) },
                                                { label: 'Net Amount', value: fmtINR(netAmount), accent: true },
                                            ] : [
                                                { label: 'Total Amount', value: fmtINR(viewed.header.po_amount), accent: true },
                                            ]),
                                            ...(isInstitutional ? [] : [
                                                { label: 'Channel', value: viewed.header.channel ?? '—' },
                                                { label: 'KAM', value: viewed.header.kam_name ?? '—' },
                                                { label: 'PO Expiry', value: formatDate(viewed.header.po_expiry_date) },
                                            ]),
                                        ].map((item, i) => (
                                            <div key={i}>
                                                <div style={S.fieldLabel}>{item.label}</div>
                                                <div style={{ fontSize: 14, fontWeight: item.accent ? 700 : 600, color: item.accent ? '#6E58A6' : '#231C30' }}>{item.value}</div>
                                            </div>
                                        ))}
                                    </div>

                                    {isInstitutional && (
                                        <div style={{ ...S.fieldGrid, borderTop: 'none', background: '#FBF9FE' }}>
                                            <div style={{ gridColumn: '1 / -1', fontSize: 11, fontWeight: 700, color: '#B05770', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Institution Details</div>
                                            {[
                                                { label: 'Sub Category', value: viewed.header.sub_category ?? '—' },
                                                { label: 'GSTIN', value: viewed.header.gstin ?? '—' },
                                                { label: 'Contact Person', value: viewed.header.contact_person ?? '—' },
                                                { label: 'Mobile', value: viewed.header.mobile ?? '—' },
                                                { label: 'Buyer Address', value: viewed.header.buyer_address ?? '—', wide: true },
                                                { label: 'Consignee Address', value: viewed.header.consignee_address ?? '—', wide: true },
                                            ].map((item, i) => (
                                                <div key={i} style={item.wide ? { gridColumn: '1 / -1' } : undefined}>
                                                    <div style={S.fieldLabel}>{item.label}</div>
                                                    <div style={{ fontSize: 13.5, fontWeight: 600, color: '#231C30', whiteSpace: 'pre-line' }}>{item.value}</div>
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    <div style={{ flex: 1, overflowY: 'auto', minHeight: 0, padding: '16px 20px' }}>
                                        <h4 style={S.sectionTitle}>Line Items</h4>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                            {viewed.lines.map(li => (
                                                <div key={li.id} style={S.lineCard}>
                                                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                                                        <div style={{ minWidth: 0 }}>
                                                            <div style={{ fontWeight: 700, fontSize: 13.5, wordBreak: 'break-word' }}>{li.product_name}</div>
                                                            <div style={{ fontSize: 11.5, color: '#8C82A0' }}>EAN {li.ean_code}</div>
                                                            {li.edit_remarks && Object.keys(li.edit_remarks).length > 0 && (
                                                                <div style={{ fontSize: 11, color: '#C25E77', marginTop: 3 }}>
                                                                    {Object.entries(li.edit_remarks).map(([f, r]) => r && (
                                                                        <div key={f}>⚠ {FIELD_LABELS[f] || f} changed: {r}</div>
                                                                    ))}
                                                                </div>
                                                            )}
                                                        </div>
                                                        <div style={{ fontWeight: 700, fontSize: 13.5, flexShrink: 0 }}>{fmtINR(li.po_amount)}</div>
                                                    </div>
                                                    <div style={S.statChipRow}>
                                                        <span style={S.statChip}>MRP {fmtINR(li.mrp)}</span>
                                                        {isInstitutional ? (
                                                            <>
                                                                <span style={S.statChip}>GST {li.gst_percent != null ? `${li.gst_percent}%` : '—'}</span>
                                                                <span style={S.statChip}>HSN {li.hsn_number || '—'}</span>
                                                            </>
                                                        ) : (
                                                            <>
                                                                <span style={S.statChip}>Case Pack {li.case_qty ?? '—'}</span>
                                                                <span style={S.statChip}>Billing {fmtINR(li.billing_price)}</span>
                                                                {hasRegions && uniqueRegionKeys.map(r => (
                                                                    <span key={r} style={S.statChip}>{r} {fmtNum(li.region_qty?.[r])}</span>
                                                                ))}
                                                            </>
                                                        )}
                                                        <span style={S.statChip}>Qty {li.po_qty}</span>
                                                    </div>
                                                </div>
                                            ))}
                                            {viewed.lines.length === 0 && (
                                                <p style={{ ...S.empty, padding: '20px 0' }}>No line items.</p>
                                            )}
                                        </div>
                                    </div>
                                </>
                            );
                        })()}
                    </div>
                </div>
            )}
        </div>
    );
}

const S = {
    page: { maxWidth: 900, margin: '0 auto', padding: '16px 8px 40px', fontFamily: "'Poppins', 'Segoe UI', sans-serif", color: '#231C30' },
    headerRow: { display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center', justifyContent: 'space-between' },
    h1: { fontSize: 22, fontWeight: 700, margin: 0 },
    search: { flex: '1 1 220px', minWidth: 0, padding: '10px 14px', borderRadius: 10, border: '1px solid #E4DEEE', fontSize: 13.5, outline: 'none', fontFamily: 'inherit' },
    sub: { fontSize: 13, color: '#8C82A0', margin: '8px 0 18px' },
    empty: { padding: '40px 0', textAlign: 'center', color: '#9A90AE', fontSize: 13.5 },
    cardList: { display: 'flex', flexDirection: 'column', gap: 10 },
    card: { textAlign: 'left', width: '100%', border: '1px solid #ECE7F2', borderRadius: 14, background: '#fff', padding: '14px 16px', cursor: 'pointer', fontFamily: 'inherit', display: 'flex', flexDirection: 'column', gap: 8 },
    cardMetaRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 12.5, color: '#5B5468' },
    chip: { display: 'inline-block', padding: '2px 8px', borderRadius: 999, fontSize: 11, fontWeight: 700 },
    overlay: { position: 'fixed', inset: 0, background: 'rgba(35,28,48,0.45)', zIndex: 200, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' },
    sheet: { width: '100%', maxWidth: 720, maxHeight: '92vh', background: '#fff', borderRadius: '18px 18px 0 0', display: 'flex', flexDirection: 'column', overflow: 'hidden' },
    sheetHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '18px 20px', flex: 'none', borderBottom: '1px solid #ECE7F2' },
    closeBtn: { border: 'none', background: '#F1EEF6', color: '#6B6379', width: 32, height: 32, borderRadius: 10, fontSize: 14, cursor: 'pointer', flexShrink: 0 },
    fieldGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: '14px 16px', padding: '16px 20px', borderBottom: '1px solid #ECE7F2', flex: 'none' },
    fieldLabel: { fontSize: 10, fontWeight: 700, color: '#8C82A0', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 },
    sectionTitle: { fontSize: 12.5, fontWeight: 700, color: '#231C30', margin: '0 0 12px', textTransform: 'uppercase', letterSpacing: '0.03em' },
    table: { width: '100%', borderCollapse: 'collapse', fontSize: 12.5, minWidth: 520 },
    th: { textAlign: 'left', padding: '8px 10px', background: '#F4F1F8', fontSize: 10.5, fontWeight: 700, color: '#5B5468', whiteSpace: 'nowrap' },
    td: { padding: '8px 10px', borderBottom: '1px solid #F1EEF6', whiteSpace: 'nowrap' },
    lineCard: { border: '1px solid #ECE7F2', borderRadius: 12, padding: '10px 12px' },
    statChipRow: { display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 },
    statChip: { fontSize: 11, fontWeight: 600, color: '#5B5468', background: '#F4F1F8', borderRadius: 999, padding: '4px 9px', whiteSpace: 'nowrap' },
};

const EMBEDDED_CSS = `
.pi-card { transition: box-shadow .15s ease, transform .1s ease; }
.pi-card:hover { box-shadow: 0 4px 14px rgba(35,28,48,0.08); }
.pi-card:active { transform: scale(0.99); }
@media (max-width: 640px) {
    .pi-page { margin-left: -22px; margin-right: -22px; padding-left: 10px !important; padding-right: 10px !important; }
}
@media (min-width: 640px) {
    .pi-search { min-width: 260px !important; flex: 0 1 auto !important; }
}
`;
